/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, memo, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Link from '@atlaskit/link';
import { Box, Grid, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { ErrorBoundary } from '../../../common/ui/error-boundary';

import { AddPinModal } from './add-pin/add-pin-modal';
import { onAddBookmarkShown, onLearnMoreAboutPinsClick } from './analytics';
import { messages } from './messages';

const styles = cssMap({
	container: {
		gridArea: 'results-list',
		borderRadius: token('radius.small'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		backgroundColor: token('color.background.accent.blue.subtlest'),
	},
	grid: {
		columnGap: token('space.0'),
		gridTemplateAreas: '"card-copy add-pin" "link-container add-pin"',
		gridTemplateColumns: '1fr auto',
	},
	cardCopy: {
		gridArea: 'card-copy',
	},
	addPin: {
		gridArea: 'add-pin',
		justifySelf: 'flex-end',
		alignSelf: 'center',
	},
	linkContainer: {
		gridArea: 'link-container',
	},
});

const LEARN_MORE_URL =
	'https://support.atlassian.com/organization-administration/docs/add-or-edit-rovo-search-bookmarks/';

type AddPinCardProps = {
	query: string;
	cloudId: string;
	orgId: string;
	hasAdminhubAccess: boolean;
	callbacks: {
		onSuccessCallback: () => void;
		onDismissCallback: () => void;
	};
	workspaceId: string;
	product: string;
};

export const AddPinCard: import('react').MemoExoticComponent<
	({
		query,
		orgId,
		cloudId,
		callbacks,
		hasAdminhubAccess,
		workspaceId,
		product,
	}: AddPinCardProps) => JSX.Element
> = memo(
	({
		query,
		orgId,
		cloudId,
		callbacks,
		hasAdminhubAccess,
		workspaceId,
		product,
	}: AddPinCardProps) => {
		const [fireAnalyticsEvent] = useAnalytics();
		const { formatMessage } = useIntl();
		const [showModal, setShowModal] = useState(false);

		const onDismissClicked = () => callbacks.onDismissCallback();

		useEffect(
			() => {
				fireAnalyticsEvent(
					onAddBookmarkShown({
						query,
					}),
				);
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[],
		);

		return (
			<ErrorBoundary fallback={<Fragment />}>
				<Box xcss={styles.container}>
					<Grid xcss={styles.grid}>
						<Box xcss={styles.cardCopy}>
							<Text>{formatMessage(messages.add_pin_card_copy)}</Text>
						</Box>
						<Box xcss={styles.addPin}>
							<Button spacing="compact" onClick={() => setShowModal(true)}>
								{formatMessage(messages.add_pin_button_label)}
							</Button>
						</Box>
						<Box xcss={styles.linkContainer}>
							<Inline space="space.100">
								<Link
									href={LEARN_MORE_URL}
									onClick={() => fireAnalyticsEvent(onLearnMoreAboutPinsClick())}
								>
									{formatMessage(messages.learn_about_pins)}
								</Link>
								<Text>•</Text>
								{/* eslint-disable-next-line @atlassian/a11y/anchor-is-valid */}
								<Link href="" onClick={onDismissClicked}>
									{formatMessage(messages.dismiss)}
								</Link>
							</Inline>
						</Box>
					</Grid>
				</Box>
				{showModal && (
					<AddPinModal
						query={query}
						handleClose={() => setShowModal(false)}
						orgId={orgId}
						cloudId={cloudId}
						hasAdminhubAccess={hasAdminhubAccess}
						onSuccessCallback={callbacks.onSuccessCallback}
						workspaceId={workspaceId}
						product={product}
						source="adminOnboarding"
					/>
				)}
			</ErrorBoundary>
		);
	},
);
